import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyBrandState } from "@/components/shared/EmptyBrandState";
import { PageTitle } from "@/components/shared/PageTitle";
import { ABTestKPIs } from "./ABTestKPIs";
import { SearchFilterBar } from "@/components/shared/SearchFilterBar";
import { ABTestTable, ABTestWithProduct } from "./ABTestTable";
import { toast } from "@/hooks/use-toast";
import { useAppData } from "@/contexts/AppDataContext";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";
import { BrandSignalsView } from "./BrandSignalsView";
import type { HeroAbTestUpdatePayload } from "@/resources/product/product.api";
import type { ABTest, TestOutcome } from "@/types/ab-tests";
import { productApi } from "@/resources/product";
import { handleApiError } from "@/utils";
import { parseHeroAsinProductId } from "@/utils/ab-test-utils";
import { RoutePath } from "@/routes";
import type { CreateABTestLocationState } from "@/pages/CreateABTest";

export function ABTestsContent() {
  const readOnly = useViewerReadOnly();
  const navigate = useNavigate();
  const { selectedBrandId } = useAppData();
  const [activeTab, setActiveTab] = useState<"tests" | "signals">("signals");
  const { data: brandAsins = [] } = productApi.useGetHeroProductList(selectedBrandId ?? undefined);

  const { data: heroAsins = [], isPending: isAbTestsPending } = productApi.useGetBrandHeroAbTests(selectedBrandId);
  const updateTestMutation = productApi.useUpdateHeroAbTest();
  const deleteTestMutation = productApi.useDeleteHeroAbTest();
  const uploadResultsMutation = productApi.useUploadHeroAbTestResults();

  const [search, setSearch] = useState("");
  const [multiFilters, setMultiFilters] = useState<Record<string, string[]>>({
    product: [],
    status: [],
    result: [],
    scope: [],
  });

  const handleDeleteTest = async (testId: string) => {
    if (selectedBrandId == null) return;
    const hit = allTests.find((t) => t.id === testId);
    const productId = hit?.heroProductId;
    if (productId == null) {
      toast({ title: "Cannot delete", description: "Missing product context for this test.", variant: "destructive" });
      return;
    }
    try {
      await deleteTestMutation.mutateAsync({ productId, testId, brandId: selectedBrandId });
      toast({ title: "Test deleted", description: "The A/B test has been removed." });
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleDeleteTests = async (testIds: string[]) => {
    if (selectedBrandId == null) return;
    try {
      for (const testId of testIds) {
        const hit = allTests.find((t) => t.id === testId);
        const productId = hit?.heroProductId;
        if (productId == null) continue;
        await deleteTestMutation.mutateAsync({ productId, testId, brandId: selectedBrandId });
      }
      toast({
        title: "Tests deleted",
        description: `${testIds.length} ${testIds.length === 1 ? "test has" : "tests have"} been removed.`,
      });
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleUpdateTest = async (args: { productId: number; testId: string; body: HeroAbTestUpdatePayload }) => {
    if (selectedBrandId == null) {
      toast({ title: "Brand required", description: "Select a brand before saving.", variant: "destructive" });
      throw new Error("Brand required");
    }
    try {
      await updateTestMutation.mutateAsync({
        productId: args.productId,
        testId: args.testId,
        body: { ...args.body, brand_id: selectedBrandId },
      });
    } catch (e) {
      handleApiError(e);
      throw e;
    }
  };

  const handleUploadResults = async (testId: string, file: File, outcome?: TestOutcome, variantBSwitchDate?: string) => {
    if (selectedBrandId == null) return;
    const hit = allTests.find((t) => t.id === testId);
    const productId = hit?.heroProductId;
    if (productId == null) {
      toast({ title: "Cannot upload", description: "Missing product context for this test.", variant: "destructive" });
      return;
    }
    try {
      const res = await uploadResultsMutation.mutateAsync({
        productId,
        testId,
        brandId: selectedBrandId,
        file,
        outcome,
        variantBSwitchDate,
      });
      if (res.parsed) {
        toast({
          title: "Results imported",
          description: `Metrics saved. Outcome: ${res.outcome}.`,
        });
      } else {
        toast({
          title: "Test marked complete",
          description: res.message || "The CSV could not be parsed. Outcome set to Inconclusive.",
          variant: "destructive",
        });
      }
    } catch (e) {
      handleApiError(e);
    }
  };

  const allTests: ABTestWithProduct[] = useMemo(() => {
    return heroAsins.flatMap((asin) => {
      const productId = parseHeroAsinProductId(asin.id);
      let tests = asin.tests ?? [];
      if (search.trim()) {
        const q = search.toLowerCase();
        tests = tests.filter(
          (t) =>
            (t.targetKeyword ?? "").toLowerCase().includes(q) ||
            (t.targetCluster ?? "").toLowerCase().includes(q) ||
            t.intentShift.from.toLowerCase().includes(q) ||
            t.intentShift.to.toLowerCase().includes(q) ||
            asin.title.toLowerCase().includes(q),
        );
      }
      return tests?.map((t) => ({
        ...t,
        heroProductId: productId ?? undefined,
        heroProductName: asin.title,
        heroProductImage: asin.imageUrl,
        heroProductTitle: asin.title,
        heroBulletPoints: asin.bulletPoints,
        heroAsin: asin.asin,
      }));
    }).filter((t) => {
      if (multiFilters.product.length > 0 && !multiFilters.product.includes(t.heroProductName || "")) return false;
      if (multiFilters.status.length > 0) {
        const displayStatus =
          t.status === "building" ? "building" : t.status === "draft" || t.status === "paused" ? "planning" : t.status === "completed" ? "complete" : t.status;
        if (!multiFilters.status.includes(displayStatus)) return false;
      }
      if (multiFilters.result.length > 0) {
        if (!t.outcome) return false;
        if (!multiFilters.result.includes(t.outcome)) return false;
      }
      if (multiFilters.scope.length > 0) {
        const scopeFields = t.scopeFields || [t.contentType];
        const hasScope = scopeFields.some((f) => {
          if (multiFilters.scope.includes("title") && f === "title") return true;
          if (multiFilters.scope.includes("bullets") && f.startsWith("bullet")) return true;
          return false;
        });
        if (!hasScope) return false;
      }
      return true;
    });
  }, [heroAsins, search, multiFilters]);

  const heroProducts = useMemo(() => {
    const seen = new Set<string>();
    return heroAsins
      .filter((a) => {
        if (seen.has(a.title)) return false;
        seen.add(a.title);
        return true;
      })
      .map((a) => ({ name: a.title, imageUrl: a.imageUrl }));
  }, [heroAsins]);

  const handleResumeDraft = (test: ABTest) => {
    const testWithProduct = allTests.find((t) => t.id === test.id);
    const matchedAsin = heroAsins.find((a) => parseHeroAsinProductId(a.id) === testWithProduct?.heroProductId) ?? heroAsins[0];
    if (!matchedAsin) return;
    const productId = parseHeroAsinProductId(matchedAsin.id);
    if (productId == null) return;
    const state: CreateABTestLocationState = {
      heroAsin: matchedAsin,
      productId,
      prefillFromDraft: test,
      returnTo: window.location.pathname + window.location.search,
    };
    navigate(RoutePath.CreateABTest, { state });
  };

  if (brandAsins.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <PageTitle title="Brand Signals" brandScoped />
          <p className="text-muted-foreground mt-1">Cross-product insights and hypotheses validated through experimentation.</p>
        </div>
        <EmptyBrandState pageName="Brand Signals" skeleton="brand-signals" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <PageTitle title="Brand Signals" brandScoped />
        <p className="text-muted-foreground mt-1">
          Cross-product insights and hypotheses validated through experimentation.
        </p>
      </div>

      <ABTestKPIs brandId={selectedBrandId} />

      <div className="bg-card rounded-[4px] border border-border">
        <div className="px-5 pt-5">
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "tests" | "signals")}>
            <TabsList variant="segmented" className="w-auto">
              <TabsTrigger variant="segmented" value="signals">Brand Signals</TabsTrigger>
              <TabsTrigger variant="segmented" value="tests">A/B Tests</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {activeTab === "tests" ? (
          <div className="p-6 space-y-4">
            <SearchFilterBar
              search={search}
              onSearchChange={setSearch}
              placeholder="Search tests..."
              filterColumns={[
                { id: "product", label: "Product", options: heroProducts?.map(p => ({ value: p.name, label: p.name.length > 25 ? p.name.slice(0, 25) + "\u2026" : p.name, imageUrl: p.imageUrl })) },
                { id: "status", label: "Status", options: [{ value: "building", label: "Drafting" }, { value: "planning", label: "Planning" }, { value: "running", label: "Running" }, { value: "complete", label: "Complete" }, { value: "archived", label: "Archived" }] },
                { id: "result", label: "Result", options: [{ value: "control", label: "Variant A Won" }, { value: "variant", label: "Variant B Won" }, { value: "inconclusive", label: "Inconclusive" }] },
                { id: "scope", label: "Scope", options: [{ value: "title", label: "Title" }, { value: "bullets", label: "Bullets" }] },
              ]}
              activeFilters={multiFilters}
              onFiltersChange={setMultiFilters}
            />

            <ABTestTable
              tests={allTests}
              currentTitle={heroAsins[0]?.title || ""}
              bulletPoints={heroAsins[0]?.bulletPoints || []}
              onDeleteTest={handleDeleteTest}
              onDeleteTests={handleDeleteTests}
              onUploadResults={handleUploadResults}
              onUpdateTest={handleUpdateTest}
              onResumeDraft={handleResumeDraft}
              showProductColumn
              readOnly={readOnly}
            />
          </div>
        ) : (
          <BrandSignalsView />
        )}
      </div>
    </div>
  );
}
